import { z } from "zod";
import { TaxResidenceSchema, UserSchema, type User } from "../treezor/index.js";

export const treezorEffectiveBeneficiarySharesPercent = z
  .string()
  .trim()
  .nonempty()
  .regex(/^\d+(\.\d+)?$/)
  .transform((x) => parseFloat(x))
  .pipe(z.number().min(0).max(100))
  .transform((x) => x.toString());

export const treezorEffectiveBeneficiaryControllingPersonType = z.union([
  z.literal(1), // Shareholder
  z.literal(2), // Other relationship
]);

export const isTreezorEffectiveBeneficiarySharesComplete = (data: {
  controllingPersonType: z.infer<typeof treezorEffectiveBeneficiaryControllingPersonType>;
  sharesPercent?: string;
}) => data.controllingPersonType !== 1 || data.sharesPercent !== undefined;

export const treezorEffectiveBeneficiaryPepFields = {
  isPep: z.boolean(),
  occupationCategory: UserSchema.shape.occupationCategory,
  monthlyIncomeRange: UserSchema.shape.monthlyIncomeRange,
  personalAssetsRange: UserSchema.shape.personalAssetsRange,
};

export const isTreezorEffectiveBeneficiaryPepComplete = (
  data: Pick<User, "occupationCategory" | "monthlyIncomeRange" | "personalAssetsRange"> & {
    isPep: boolean;
  },
) =>
  !data.isPep ||
  [data.occupationCategory, data.monthlyIncomeRange, data.personalAssetsRange].every(
    (value) => value !== undefined && value !== null,
  );

export default {
  params: z
    .object({
      accountId: z.string(),
      specifiedUSPerson: z.boolean(),
      title: UserSchema.shape.title,
      firstname: UserSchema.shape.firstname,
      lastname: UserSchema.shape.lastname,
      middleNames: UserSchema.shape.middleNames,
      birthday: z.union([z.iso.date(), z.literal("")]),
      placeOfBirth: UserSchema.shape.placeOfBirth,
      birthCountry: UserSchema.shape.birthCountry,
      nationality: UserSchema.shape.nationality,
      phone: UserSchema.shape.phone,
      address1: UserSchema.shape.address1,
      address2: UserSchema.shape.address2,
      address3: UserSchema.shape.address3.unwrap(),
      postcode: UserSchema.shape.postcode,
      city: UserSchema.shape.city,
      country: UserSchema.shape.country,
      controllingPersonType: treezorEffectiveBeneficiaryControllingPersonType,
      sharesPercent: treezorEffectiveBeneficiarySharesPercent.optional(),
      isLegalRepresentative: z.boolean(),
      taxResidence: z
        .object({
          country: TaxResidenceSchema.shape.country,
          taxPayerId: TaxResidenceSchema.shape.taxPayerId.unwrap().nullish(),
        })
        .nullish(),
      ...treezorEffectiveBeneficiaryPepFields,
    })
    .refine(isTreezorEffectiveBeneficiarySharesComplete, {
      path: ["sharesPercent"],
      message: "sharesPercent is required for shareholders",
    })
    .refine(isTreezorEffectiveBeneficiaryPepComplete, {
      path: ["isPep"],
      message:
        "occupationCategory, monthlyIncomeRange and personalAssetsRange are required for PEP",
    })
    .refine((data) => !data.specifiedUSPerson || data.taxResidence?.country === "US", {
      path: ["taxResidence", "country"],
      message: "US tax residence is required for specified US persons",
    })
    .refine(
      (data) =>
        !data.taxResidence || data.taxResidence.country === "FR" || !!data.taxResidence.taxPayerId,
      {
        path: ["taxResidence", "taxPayerId"],
        message: "taxPayerId is required when the tax residence country is not FR",
      },
    ),
  response: z.void(),
};
